import { Trash2, Edit, Copy } from "lucide-react";
import BlindRender from "../components/BlindRender";
import { useSelector } from "react-redux";

export const CartItem = ({
	item,
	index,
	onQuantityChange,
	onRemove,
	onEdit,
	onDuplicate,
}) => {
	const country = useSelector((state) => state.country);
	const discount =
		parseFloat(item?.productInfo?.Discount) > 0 ||
		parseFloat(item?.productInfo?.Global_Discount) > 0;

	const getHemRenderPath = (productInfo) => {
		let type = "";
		if (
			productInfo?.Bottom_Bar?.includes("Wrapped") &&
			productInfo?.Bottom_Bar?.includes("Accubar")
		) {
			type = "wab";
		} else if (productInfo?.Bottom_Bar?.includes("Accubar")) {
			type = "ab";
		} else if (productInfo?.Bottom_Bar?.includes("Plain Hem")) {
			type = "ph";
		} else if (productInfo?.Bottom_Bar?.includes("Illusion")) {
			type = "ill";
		} else if (productInfo?.Bottom_Bar?.includes("Interlude")) {
			type = "int";
		} else {
			type = "null";
		}

		const color =
			productInfo?.Bottom_Bar === "Plain Hem"
				? "standard"
				: productInfo?.Bottom_Bar_Color?.toLowerCase();

		return `${type}/${color}`;
	};

	const valanceRender = (name, color) => {
		let code;
		switch (name?.toLowerCase()) {
			case "compact curved cassette":
			case "compact curved casette":
				code = "ccc";
				break;
			case "open roll":
				code = "or";
				break;
			case '3" fascia':
				code = "3f";
				break;
			case '4" fascia':
				code = "4f";
				break;
			case "curved cassette s":
				code = "d8";
				break;
			case "curved cassette m":
				code = "d12";
				break;
			case "curved cassette l":
				code = "d16";
				break;
			case "pvc valance":
				code = "pvc";
				break;
			case 'sb 3" wrapped fascia':
				code = "3f";
				break;
			case 'sb 4" wrapped fascia':
				code = "4f";
				break;
			case "sb wrapped curved cassette":
			case "curved cassette":
				code = "d12";
				break;
			default:
				code = "or";
		}

		return `render/valance/${code}/${color}.png`;
	};

	const getHemTypeDisplayName = (bottomBar) => {
		switch (bottomBar) {
			case "Accubar 2.1":
				return "Aluminum Hem";
			case "Interlude 2.0":
				return "Aluminum Bar";
			case "Illusion":
				return "Aluminum Bar";
			default:
				return bottomBar;
		}
	};

	const getDriveTypeDisplayName = (type) => {
		switch (type) {
			case "Zero Gravity":
				return "Chainless Manual";
			case "Chain":
				return "Chain Manual";
			case "Motor":
				return "Automated";
			default:
				return type;
		}
	};

	const getValanceTypeDisplayName = (valanceName) => {
		switch (valanceName) {
			case "Zero Gravity":
				return "Glide Chainless";
			case 'SB 3" Fascia':
				return '3" Fascia';
			case "SB Compact Curved Cassette":
				return "Compact Curved Cassette";
			case 'SB 4" Fascia':
				return '4" Fascia';
			case "SB Curved Cassette":
				return "Curved Cassette";
			default:
				return valanceName;
		}
	};

	return (
		<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
			<div className="flex flex-col md:flex-row gap-4">
				{/* Product Image */}
				<div className="w-full md:w-1/4">
					<div className="max-h-[400px] md:max-h-[200px] rounded-2xl overflow-hidden">
						<BlindRender
							renderFabric={`${item?.productInfo?.Fabric?.toLowerCase()} ${item?.productInfo?.SG_Color?.toLowerCase()}`}
							renderHem={getHemRenderPath(item?.productInfo)}
							renderValance={valanceRender(
								item.productInfo?.Valance,
								item.productInfo?.Valance_Color
							).toLowerCase()}
						/>
					</div>
				</div>

				{/* Product Details */}
				<div className="flex-1 space-y-2">
					<h3 className="font-bold">
						{item.productInfo?.New_Program || item.productInfo?.Program} |
						{item.optionInfo?.Type} {item.optionInfo?.widthWhole}{" "}
						{item.optionInfo?.widthFraction !== "0/16" &&
							item.optionInfo?.widthFraction}{" "}
						x {item.optionInfo?.lengthWhole}{" "}
						{item.optionInfo?.lengthFraction !== "0/16" &&
							item.optionInfo?.lengthFraction}
					</h3>

					<div className="flex flex-col gap-4 items-start text-sm text-gray-600">
						<div>
							<span className="font-bold">Type:</span> {item.productInfo?.Type}
						</div>
						<div>
							<span className="font-bold">Fabric:</span>{" "}
							{item.productInfo?.New_Fabric} -{" "}
							{item.productInfo?.New_Colour || item.optionInfo?.selectedColor}
						</div>
						<div>
							<span className="font-bold">Drive:</span>{" "}
							{getDriveTypeDisplayName(item.productInfo?.Operation)} -{" "}
							{item.productInfo?.position}
						</div>
						<div>
							<span className="font-bold">Valance:</span>{" "}
							{getValanceTypeDisplayName(item.productInfo?.Valance)} -{" "}
							{item.productInfo?.Valance_Color}
						</div>
						<div>
							<span className="font-bold">Bottom Bar:</span>{" "}
							{getHemTypeDisplayName(item.productInfo?.Bottom_Bar)} -{" "}
							{item.productInfo?.Bottom_Bar_Color}
						</div>
					</div>
				</div>

				{/* Price & Actions */}
				<div className="space-y-4 text-left md:text-right">
					{/* Price Display */}
					<div className="space-y-1">
						<div
							className={
								discount ? "line-through text-gray-500" : "font-medium"
							}
						>
							{country === "USA"
								? `$${item.optionInfo?.RETAIL_PRICE} USD`
								: `$${item.optionInfo?.RETAIL_PRICE} CAD`}
						</div>
						{discount && (
							<div className="text-primary font-medium">
								{country === "USA"
									? `$${item.optionInfo?.OUR_PRICE} USD`
									: `$${(item.optionInfo?.OUR_PRICE).toFixed(2)} CAD`}
							</div>
						)}
					</div>

					{/* Quantity Input */}
					<div className="flex justify-start md:justify-end items-center gap-2">
						<label className="text-sm text-gray-600">Qty:</label>
						<input
							type="number"
							min="1"
							value={item.optionInfo?.quantity || 1}
							onChange={(e) => {
								if (e.target.value > 0) {
									onQuantityChange(index, parseInt(e.target.value));
								} else {
									onQuantityChange(index, 1);
								}
							}}
							className="w-20 px-2 py-1 border border-gray-200 rounded focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
						/>
					</div>

					{/* Action Buttons */}
					<div className="flex justify-start md:justify-end gap-2">
						<button
							onClick={() => onDuplicate(index)}
							className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-xl transition-colors"
							title="Duplicate"
						>
							<Copy size={18} />
						</button>
						<button
							onClick={() => onEdit(index)}
							className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-xl transition-colors"
							title="Edit"
						>
							<Edit size={18} />
						</button>
						<button
							aria-label="Remove Item"
							onClick={() => onRemove(index)}
							className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
							title="Remove"
						>
							<Trash2 size={18} />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
